import { Map, NavigationControl, ScaleControl } from 'mapbox-gl'
import LayerManager from './layer_manager'

export default {
  readStyleUrl() {
    return 'mapbox://styles/vibrantplanet/ckqvcetmw0ceh17nbcaol29xe'
  },
  sendEvent(name, data) {
    this.sendTargetedEvent(this.el.id, name, data)
  },
  sendTargetedEvent(target, name, data) {
    this.pushEventTo(`#${target}`, name, data)
  },
  handleTargetedEvent(name, callback) {
    this.handleEvent(name, ({ target, ...rest }) => {
      if (target === this.el.dataset.target) callback(rest)
    })
  },
  setCenter(lng, lat, xOffset, yOffset) {
    if (xOffset || yOffset) {
      const center = this.map.project([lng, lat])

      this.map.setCenter(
        this.map.unproject({
          x: center.x + (xOffset || 0),
          y: center.y + (yOffset || 0)
        })
      )
    } else {
      this.map.setCenter({ lng: lng, lat: lat })
    }
  },
  mounted() {
    // NOTE: state is encoded with Elixir's snake_case names; destructure to camelCase for JS.
    const {
      center_lat: lat,
      center_lng: lng,
      max_zoom: maxZoom,
      min_zoom: minZoom,
      x_offset: xOffset,
      y_offset: yOffset,
      zoom
    } = JSON.parse(this.el.dataset.mapState)

    this.map = new Map({
      accessToken: land_tender.mapbox.public_token,
      center: { lng, lat },
      container: this.el,
      maxZoom,
      minZoom,
      style: this.readStyleUrl(),
      zoom
      // preserveDrawingBuffer: true, // to support fully rendered map (layers) in toDataURL()
      // uncomment to show the zoom level in the url for debugging
      // hash: true,
    })
      .on('load', ({ target: map }) => {
        this.sendEvent('map.loaded', {
          bounds: map.getBounds(),
          zoom: map.getZoom()
        })
        this.layerManager.loaded()
        this.loadedCallback?.()
      })
      .on('moveend', ({ target: map }) => {
        this.sendEvent('map.moveend', {
          bounds: map.getBounds(),
          zoom: map.getZoom()
        })
      })
      .on('zoomend', ({ target: map }) => {
        this.sendEvent('map.zoomend', {
          bounds: map.getBounds(),
          zoom: map.getZoom()
        })
      })
      .addControl(new NavigationControl({ showCompass: false }), 'top-left')

    this.setCenter(lng, lat, xOffset, yOffset)

    this.layerManager = new LayerManager({ hook: this, map: this.map })

    this.handleTargetedEvent(
      'set-center',
      ({ lng, lat, zoom, xOffset, yOffset }) => {
        // it's important that the zoom happens _before_ re-centering, otherwise the wrong center is zoomed into
        this.map.setZoom(zoom)
        this.setCenter(lng, lat, xOffset, yOffset)
      }
    )

    this.handleTargetedEvent('add-scale-control', (_args) => {
      this.map.addControl(new ScaleControl({ unit: 'imperial' }))
    })

    this.handleTargetedEvent('double-map-scale', (_args) => {
      const el = document.querySelector('.mapboxgl-ctrl-scale')
      if (!el) return

      const width = parseFloat(el.style.width)

      const match = el.textContent.trim().match(/^([\d,\.]+)\s*(ft|mi)$/)
      if (!match) return

      const value = parseFloat(match[1].replace(/,/g, '')) * 2

      const unit = match[2]

      if (unit === 'ft') {
        if (value >= 5280) {
          el.textContent = `${(value / 5280).toFixed(1)} mi`
        } else {
          el.textContent = `${value.toLocaleString()} ft`
        }
      } else if (unit === 'mi') {
        el.textContent = `${value.toFixed(1)} mi`
      }

      el.style.width = width * 2 + 'px'
      el.style.fontSize = '40px'
      el.style.height = '60px'
      el.style['padding-top'] = '20px'
    })

    this.handleTargetedEvent(
      'fit-bounds',
      ({ bounding_box, xOffset, yOffset, ...options }) => {
        const {
          lng_min: lng_min,
          lat_min: lat_min,
          lng_max: lng_max,
          lat_max: lat_max
        } = bounding_box
        const bbox = [
          [lng_min, lat_min],
          [lng_max, lat_max]
        ]

        const defaultOptions = {
          speed: 0.5,
          maxZoom: 14,
          padding: { top: 100, bottom: 100, left: 100, right: 100 }
        }
        options = {
          offset: [-xOffset / 2, -yOffset / 2],
          ...defaultOptions,
          ...options.options
        }

        this.map.fitBounds(bbox, options)
        this.sendEvent('map.fit_bounds', options)
      }
    )

    this.handleTargetedEvent('get-map-bounds', () => {
      this.sendEvent('map.bounds', { bounds: this.map.getBounds() })
    })

    this.mountedCallback?.()
  }
}
